import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import api from "../lib/api";
import { useT, useUI, useLang } from "../lib/i18n";
import SEO from "../components/site/SEO";

export default function Blog() {
  const { content } = useOutletContext();
  const t = useT();
  const ui = useUI();
  const lang = useLang();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/posts?published_only=true").then(({ data }) => setPosts(data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seo = (content.seo || {}).blog;

  return (
    <div data-testid="blog-page">
      <SEO seoData={seo} />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-16">
        <p className="overline mb-4">{ui.blog}</p>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-none font-light">{ui.blog}</h1>
        <p className="mt-6 text-[#4A3F35] max-w-2xl text-lg">{ui.blogIntro}</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        {posts.length === 0 ? (
          <div className="text-center py-24 text-[#4A3F35]/60">{ui.noPosts}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
                data-testid={`blog-card-${i}`}
              >
                <Link to={`/${lang}/blog/${p.slug}`} className="group block">
                  {p.cover_url && (
                    <div className="overflow-hidden rounded-3xl aspect-[4/3] mb-5 bg-[#F6F3EC]">
                      <img src={p.cover_url} alt={t(p.title)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-xs text-[#4A3F35]/60 mb-3">
                    {p.category && <span className="text-[#C87A5E] uppercase tracking-wider">{p.category}</span>}
                    {p.reading_time && <span className="flex items-center gap-1"><Clock size={11} />{p.reading_time} {ui.minRead}</span>}
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl tracking-tight leading-tight group-hover:text-[#C87A5E] transition">{t(p.title)}</h2>
                  <p className="mt-3 text-[#4A3F35] line-clamp-3">{t(p.excerpt)}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-sm text-[#C87A5E] link-underline">
                    {ui.readMore} <ArrowUpRight size={14} />
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
